import { proposalsService } from '@/services/proposals';
import type { ProposalSummary } from '@/services/proposals/types';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ProposalAnalysisListItem } from '../types';

interface Params {
  search?: string;
  pageNo: number;
  pageSize: number;
  sortField?: string;
  sortType?: string;
  status?: string;
  productCode?: string;
  /** Início do período (data da proposta), ISO `yyyy-MM-dd`. */
  proposalDateFrom?: string;
  /** Fim do período (data da proposta), ISO `yyyy-MM-dd`. */
  proposalDateTo?: string;
}

const PROPOSAL_IN_ANALYSIS_STATUS = String(PortalStatus.ProposalInAnalysis);

function mapSummaryToListItem(
  summary: ProposalSummary
): ProposalAnalysisListItem {
  return {
    id: summary.id,
    proposerName: summary.proposerName,
    proposerDocument: summary.registerNumber,
    brokerName: summary.brokerName,
    brokerDocument: summary.brokerDocument ?? '',
    product: summary.productName,
    proposalNumber: summary.proposalNumber,
    createdAt: summary.createdAt,
    transmissionDate: summary.createdAt,
    proposalStatus: summary.proposalStatus,
  };
}

export function useProposalAnalyses(params: Params) {
  return useQuery({
    queryKey: ['proposal-analyses', params],
    queryFn: async () => {
      const response = await proposalsService.getProposals({
        pageNumber: params.pageNo,
        pageSize: params.pageSize,
        search: params.search,
        // A tela é escopada em "Proposta em Análise"; sem status explícito,
        // continua caindo nesse status.
        status: params.status || PROPOSAL_IN_ANALYSIS_STATUS,
        productCode: params.productCode,
        proposalDateFrom: params.proposalDateFrom,
        proposalDateTo: params.proposalDateTo,
        sortField: params.sortField || 'index_time',
        sortType: params.sortType === 'ASC' ? 'ASC' : 'DESC',
      });

      if (!response.success || !response.data) {
        throw new Error(
          response.error || 'Erro ao carregar propostas em análise'
        );
      }

      return {
        data: response.data.data.map(mapSummaryToListItem),
        meta: {
          total: response.data.meta.total,
          totalPages: response.data.meta.totalPages,
        },
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
}
